import * as React from 'react';
import { useIntl } from 'react-intl';
import { Control, useController } from 'react-hook-form';
import { CmdError } from '@commander-services/gui-components';
import FormikService from '../../../Services/FormikService';
import useOutsideClick from '../../../hooks/useOutsideClick';
import useKeyPress, { ESCAPE_KEY } from '../../../hooks/useKeyPress';
import usePrevious from '../../../hooks/usePrevious';
import { ISimpleSelectOption } from './interfaces';
import configJs from '../../../../config';

interface ISimpleSelectHookFormProps {
  id: string;
  name: string;
  label: string;
  control: Control<any>;
  options?: ISimpleSelectOption[];
  required?: boolean;
  onChangeCallback?: (value: any) => void;
  tooltip?: string;
  addtionalButtonCallBack?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  isAdditionalButton?: boolean;
  tooltipAdditionalButton?: string;
  labelClassName?: string;
}

export default function SimpleSelectHookForm(props: ISimpleSelectHookFormProps): JSX.Element {
  const [options, setOptions] = React.useState<ISimpleSelectOption[]>(props.options || []);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [isRequired, setIsRequired] = React.useState<boolean>(false);
  const { formatMessage: f } = useIntl();

  const prevOptions = usePrevious(props.options, { jsonParse: false });
  const prevRequired = usePrevious(props.required, { jsonParse: false });

  const {
    field: { ref, onChange, onBlur, value },
    fieldState: { invalid, error },
  } = useController({
    name: props.name,
    control: props.control,
    defaultValue: '', // or the default value
  });

  const clickOutsideRef: React.RefObject<any> = React.useRef();

  useOutsideClick(clickOutsideRef, () => {
    setIsOpen(false);
  });

  useKeyPress(ESCAPE_KEY, () => {
    setIsOpen(false);
  });

  React.useEffect(() => {
    if (JSON.stringify(prevOptions) !== JSON.stringify(props.options)) {
      setOptions(props.options || []);
    }
    if (JSON.stringify(prevRequired) !== JSON.stringify(props.required)) {
      setIsRequired(props.required || false);
    }
  }, [prevOptions, prevRequired, props.options, props.required]);

  const handleOpen = (
    event:
      | React.MouseEvent<HTMLDivElement, MouseEvent>
      | React.MouseEvent<HTMLLabelElement, MouseEvent>
  ) => {
    event.preventDefault();
    setIsOpen(!isOpen);
    if (isOpen) {
      // props.form.getFieldHelpers(props.field.name).setTouched(true);
    }
  };

  const handleOnClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    const selectedValue = event.currentTarget.getAttribute('data-value');
    if (selectedValue) {
      onChange(selectedValue);
      if (props.onChangeCallback) {
        props.onChangeCallback(selectedValue);
      }
      setIsOpen(false);
    }
  };

  const handleIconClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    if (props.addtionalButtonCallBack) {
      props.addtionalButtonCallBack(event);
    }
  };

  return (
    <div
      ref={clickOutsideRef}
      className={`checkbox-group-component form-group ${error && 'form-group-error'}`}
    >
      {props.isAdditionalButton && (
        <div
          style={{
            cursor: 'pointer',
            marginTop: '-14px',
            marginBottom: '17px',
          }}
          className="text-right form-group"
        >
          <span
            onClick={handleIconClick}
            title={props.tooltipAdditionalButton ? props.tooltipAdditionalButton : ''}
          >
            <i style={{ color: '#737373' }} className="cmd-icons cmd-icon-bin" aria-hidden="true" />
            &nbsp;{f({ id: 'table.filter.remove' })}
          </span>
        </div>
        // <div className="col-auto pl-0">
        //   <a
        //     href="#"
        //     title={props.tooltipAdditionalButton ? props.tooltipAdditionalButton : ''}
        //     className="e-button e-button--gray"
        //     onClick={handleIconClick}
        //   >
        //     <i className="cmd-icon cmd-icon-bin" />
        //   </a>
        // </div>
      )}
      <div>
        <label className={`${props.labelClassName || 'd-block form-label'}`} onClick={handleOpen}>
          {isRequired && <span className="text-danger required-field-info">*</span>}
          {props.label}
        </label>
        <div className="d-block">
          <div className={`b-dropdown ${isOpen ? 'open' : ''}`}>
            <div
              id={props.id}
              data-cy={`simple-select-${props.id}`}
              className="form-control-labels"
              onClick={handleOpen}
              title={props.tooltip || ''}
            >
              {FormikService.getOptionByValue(props.options || [], value)}
            </div>
            {isOpen && (
              <div className="b-dropdown__content">
                <ul className="b-dropdown__list simple-list">
                  {options &&
                    options.map((option: ISimpleSelectOption) => (
                      <li key={`key-${props.name}-${option.value}`}>
                        <a
                          href="#"
                          title={option.item}
                          className="item"
                          data-value={option.value}
                          onClick={handleOnClick}
                          style={
                            String(option.value) === String(value)
                              ? {
                                  backgroundColor: `${configJs.selectColor.backgroundColor}`,
                                }
                              : {}
                          }
                        >
                          {option.item}
                        </a>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>

          {error && error.message && <CmdError message={error.message} />}
        </div>
      </div>
    </div>
  );
}
